// src/components/FinalCTA.js
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import suitImage from './suit.png';

export default function FinalCTA({ onContinue }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Fade in after dashboard loads
    const timer = setTimeout(() => setIsVisible(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleCopyEmail = () => {
    const email = 'bobby.tjia@optimechtools.com';
    navigator.clipboard.writeText(email).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    });
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    if (onContinue) onContinue();
  };

  if (!isVisible || isDismissed) return null;

  return (
    <div style={modalStyles.overlay} onClick={(e) => e.target === e.currentTarget && handleDismiss()}>
      <div style={modalStyles.modal}>
        {/* Close button */}
        <button onClick={handleDismiss} style={modalStyles.closeBtn}>
          <X size={20} />
        </button>

        {/* Content */}
        <div style={modalStyles.content}>
          <div style={modalStyles.iconContainer}>
            <img 
              src={suitImage} 
              alt="Bobby Tjia" 
              style={modalStyles.iconImage} 
            />
          </div>

          <h2 style={modalStyles.headline}>
            Ready for us to do this for your clinic?
          </h2>

          <p style={modalStyles.subheadline}>
            Recall 10 patients in 14 days at no cost. 
          </p>

          <div style={modalStyles.divider} />

          <button 
            onClick={handleCopyEmail}
            style={{
              ...modalStyles.primaryBtn,
              background: isCopied ? '#2D9B8A' : '#1A3A3A',
            }}
          >
            {isCopied ? (
              <>
                <span style={modalStyles.checkmark}>✓</span>
                bobby.tjia@optimechtools.com Copied!
              </>
            ) : (
              'Email me for a Free Strategy Call'
            )}
          </button>

          <button 
            onClick={handleDismiss}
            style={modalStyles.secondaryBtn}
          >
            Continue Exploring Dashboard
          </button>

          <p style={modalStyles.footerText}>
            No spam. Just a conversation to see if we can help.
          </p>
        </div>
      </div>
    </div>
  );
}

const modalStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(7, 14, 22, 0.7)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    animation: 'fadeIn 0.5s ease-out',
  },
  modal: {
    background: '#FFFFFF',
    borderRadius: '20px',
    padding: '40px 36px 32px',
    maxWidth: '520px',
    width: '100%',
    position: 'relative',
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.4)',
    animation: 'slideUp 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  closeBtn: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#B5C8C4',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '6px',
    transition: 'color 0.2s',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  iconContainer: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
    overflow: 'hidden',
  },
  iconImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '50%',
  },
  headline: {
    fontSize: '26px',
    fontWeight: 700,
    color: '#1A3A3A',
    margin: '0 0 12px 0',
    fontFamily: "'Inter', -apple-system, sans-serif",
    letterSpacing: '-0.5px',
    lineHeight: 1.2,
  },
  subheadline: {
    fontSize: '18px',
    fontWeight: 500,
    color: '#2D9B8A',
    margin: '0 0 20px 0',
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
  divider: {
    width: '60px',
    height: '2px',
    background: '#E8ECE9',
    marginBottom: '24px',
  },
  primaryBtn: {
    width: '100%',
    padding: '14px 24px',
    background: '#1A3A3A',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Inter', -apple-system, sans-serif",
    transition: 'all 0.3s ease',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  checkmark: {
    fontSize: '18px',
  },
  secondaryBtn: {
    width: '100%',
    padding: '12px 24px',
    background: 'transparent',
    color: '#7A9A92',
    border: '1px solid #DDDEDC',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'Inter', -apple-system, sans-serif",
    transition: 'all 0.3s ease',
  },
  footerText: {
    fontSize: '12px',
    color: '#B5C8C4',
    margin: '16px 0 0 0',
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
};

// Add keyframes if not already present
const modalStyleSheet = document.createElement("style");
modalStyleSheet.textContent = `
  @keyframes fadeIn {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }
  @keyframes slideUp {
    0% { opacity: 0; transform: translateY(30px) scale(0.96); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
`;
document.head.appendChild(modalStyleSheet);